import type { TRequiredAccess } from "@/types/common/access.ts";

import { computed } from "vue";

import { useAuthenticationStore } from "@/stores/authentication/authentication-store.ts";

export function useUserAccess() {
  const authenticationStore = useAuthenticationStore();

  function hasAccess(access: TRequiredAccess | Array<TRequiredAccess>) {
    if (Array.isArray(access)) {
      return hasAllAccess(access);
    }

    return authenticationStore.hasUserAccess(access);
  }

  function hasAnyAccess(access: Array<TRequiredAccess>) {
    return access.some((_access) => authenticationStore.hasUserAccess(_access));
  }

  function hasAllAccess(access: Array<TRequiredAccess>) {
    return access.every((_access) => authenticationStore.hasUserAccess(_access));
  }

  function hasAccessToSubscriberContent(content: string) {
    return hasAccess({
      service: "subscriberManagement",
      function: "subscriber",
      content,
    });
  }

  function hasAccessToSubscriberModulatorContent(content: string) {
    return hasAccess({
      service: "subscriberManagement",
      function: "modulator",
      content,
    });
  }
  function canModifySubscriberAttributes() {
    return hasAccess({
      service: "subscriberManagement",
      function: "attribute",
      content: "editAttribute",
    });
  }

  const canPrivilege = computed(() => {
    return hasAccessToSubscriberContent("psbManagement");
  });

  const canModifySubscriber = computed(() => {
    return (
      hasAccessToSubscriberContent("displayImportantInfos") && hasAccessToSubscriberContent("subscriberCoordModify")
    );
  });

  const canModifySite = computed(() => {
    return hasAccess({
      service: "subscriberManagement",
      function: "site",
      content: "modifySite",
    });
  });

  const canModifySubscription = computed(() => {
    return hasAccess({
      service: "subscriberManagement",
      function: "subscription",
      content: "modifySubscription",
    });
  });

  return {
    hasAccess,
    hasAnyAccess,
    hasAllAccess,
    hasAccessToSubscriberContent,
    hasAccessToSubscriberModulatorContent,
    canModifySubscriberAttributes,
    canModifySubscriber,
    canModifySite,
    canModifySubscription,
    canPrivilege,
  };
}
